import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { api, fmtSigned } from "@/lib/journal";

export const EquityCurve = ({ data, portfolioId }) => {
  const [showBtc, setShowBtc] = useState(false);
  const [bench, setBench] = useState(null);
  const [loadingBench, setLoadingBench] = useState(false);

  useEffect(() => {
    if (!showBtc) return;
    const params = portfolioId && portfolioId !== "all" ? { portfolio_id: portfolioId } : {};
    setLoadingBench(true);
    api.get("/analytics/benchmark", { params })
      .then(({ data }) => setBench(data))
      .catch(() => setBench(null))
      .finally(() => setLoadingBench(false));
  }, [showBtc, portfolioId, data]);

  const rows = showBtc && bench?.available ? bench.points : data;

  return (
    <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="equity-curve-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
          Cumulative Realized PnL
        </h3>
        <div className="flex items-center gap-4">
          <button
            data-testid="benchmark-toggle"
            onClick={() => setShowBtc((s) => !s)}
            className={`border px-2.5 py-1 text-[10px] uppercase tracking-widest transition-colors duration-200 ${
              showBtc
                ? "border-[#C58361] bg-[#C58361]/10 text-[#C58361]"
                : "border-[#1F1F1F] text-zinc-500 hover:text-zinc-200"
            }`}
          >
            vs BTC
          </button>
          <span className="font-mono text-sm text-[#C58361]">
            {data?.length ? fmtSigned(data[data.length - 1].pnl) : "—"}
          </span>
        </div>
      </div>

      {showBtc && loadingBench && !bench && (
        <p className="mt-3 font-mono text-[11px] text-zinc-600" data-testid="benchmark-loading">
          Fetching BTC benchmark…
        </p>
      )}
      {showBtc && bench?.available && (
        <p className="mt-3 font-mono text-[11px] text-zinc-500" data-testid="benchmark-summary">
          BTC {bench.btc_change_pct >= 0 ? "+" : ""}{bench.btc_change_pct}% over the same window ·
          you are {bench.outperformance >= 0 ? "ahead by" : "behind by"}{" "}
          <span className={bench.outperformance >= 0 ? "text-[#4ADE80]" : "text-[#EF4444]"}>
            {fmtSigned(Math.abs(bench.outperformance))}
          </span>{" "}
          on a {fmtSigned(bench.notional).replace("+", "")} notional
        </p>
      )}
      {showBtc && bench && !bench.available && (
        <p className="mt-3 font-mono text-[11px] text-zinc-600" data-testid="benchmark-unavailable">
          Benchmark needs at least one closed trade and a live BTC price feed.
        </p>
      )}

      <div className="mt-6 h-[220px] min-h-[220px] w-full">
        {rows && rows.length > 0 ? (
          <ResponsiveContainer width="100%" height={220} minWidth={0} minHeight={220}>
            <AreaChart data={rows} margin={{ left: -18, right: 8, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="tit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C58361" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#C58361" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1F1F1F" vertical={false} />
              <XAxis dataKey="date" stroke="#52525B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#1F1F1F" }} />
              <YAxis stroke="#52525B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid #1F1F1F", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 12 }}
                labelStyle={{ color: "#71717A" }}
              />
              {showBtc && bench?.available && (
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono", color: "#71717A" }} />
              )}
              <Area type="monotone" dataKey="pnl" name="Fund" stroke="#C58361" strokeWidth={2} fill="url(#tit)" />
              {showBtc && bench?.available && (
                <Line type="monotone" dataKey="btc_pnl" name="BTC" stroke="#F7931A" strokeWidth={2} strokeDasharray="4 3" dot={false} />
              )}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center border border-dashed border-[#1F1F1F] text-sm text-zinc-600">
            Close a trade to plot the equity curve.
          </div>
        )}
      </div>
    </div>
  );
};
